import { Countries } from '@/comm'
import { useDatePicker } from '@/components/MyDatePicker'
import { usePid2 } from '@/components/DropdownPid'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { formatPercent, formatUSD } from '@/lib/format-utils'
import { getDateRange } from '@/lib/date-utils'
import { Button } from '@/components/ui/button'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
    getCoreRowModel,
    getFilteredRowModel,
    useReactTable,
} from '@tanstack/react-table'
import {
    useColumnFilters,
    VSWeekSelector,
} from '@/components/data-table-selectors'
import useSWR from 'swr'
import Td2 from '../../../components/zthd/Td2'
import { columnDate } from './columns'

const useColumnVisibilityStore = createColumnVisibilityStore({}, 'ccp')

const countryColumn = (code, name) => ({
    id: `${name}`,
    header: `${name}`,
    accessorFn: (row) => {
        const costs = row.costs?.[code]?.costs || 0
        const revenues = row.revenues?.[code] || 0
        const profit = revenues - costs

        const costsTotal = row.costs?.total?.costs || 0
        const revenuesTotal = row.revenues?.total || 0
        const profitTotal = revenuesTotal - costsTotal

        return { curr: profit, total: profitTotal }
    },
    cell: (row) => {
        const { curr, total } = row.getValue()
        const formatted = formatUSD(curr, 0, 0)
        const per = formatPercent(curr / total)
        return <Td2 line2={per}>{formatted}</Td2>
    },
})

const Home = () => {
    const startDate = useDatePicker((state) => state.startDate)
    const endDate = useDatePicker((state) => state.endDate)
    const pids = usePid2((state) => state.pids)

    const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore()
    const columnFilters = useColumnFilters((state) => state.columnFilters)

    const { data: revenues, mutate: mutateR } = useSWR([
        '/revenues/countrycontrast',
        { startDate, endDate, pid: pids.join(',') },
    ])

    const { data: costs, mutate: mutateC } = useSWR([
        '/costs/countrycontrast',
        { startDate, endDate, pid: pids.join(',') },
    ])

    const data = useMemo(() => {
        const dateRange = getDateRange(dayjs(startDate), dayjs(endDate))

        return dateRange.map((date) => {
            const originalCosts = costs?.[date]
            const originalRevenues = revenues?.[date]

            // Process costs data
            let processedCosts = originalCosts
            if (originalCosts) {
                const totalCosts = Object.values(originalCosts).reduce((sum, country) => {
                    return sum + (country?.costs || 0)
                }, 0)

                const totalInstalls = Object.values(originalCosts).reduce((sum, country) => {
                    return sum + (country?.installs || 0)
                }, 0)

                processedCosts = {
                    ...originalCosts,
                    total: {
                        costs: totalCosts,
                        installs: totalInstalls
                    }
                }
            }

            // Process revenues data
            let processedRevenues = originalRevenues
            if (originalRevenues) {
                const totalRevenues = Object.values(originalRevenues).reduce((sum, value) => {
                    return sum + (value || 0)
                }, 0)

                processedRevenues = {
                    ...originalRevenues,
                    total: totalRevenues
                }
            }

            return {
                date,
                costs: processedCosts,
                revenues: processedRevenues,
            }
        })
    }, [startDate, endDate, costs, revenues])

    const columns = useMemo(() => {
        const countries = Countries.filter(({ code }) => code !== 'total')
        const rows = []
        rows.push(columnDate)
        rows.push(countryColumn('total', 'Total'))
        countries.forEach((country) => {
            const { code, name } = country
            rows.push(countryColumn(code, name))
        })
        return rows
    }, [])

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        enableMultiRowSelection: true,
        state: {
            columnFilters,
            columnVisibility,
        },
    })

    const refresh = () => {
        mutateR({ revalidate: false })
        mutateC({ revalidate: false })
    }

    return (
        <>
            <div className="flex items-center gap-1 py-4">
                <VSWeekSelector />
                <Button onClick={refresh}>refresh</Button>
                <DataTableViewOptions table={table} />
            </div>
            <DataTableWithBorder table={table} />
        </>
    )
}

export default Home 